import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CircularProgress,
  Alert,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogContentText,
  DialogActions,
  IconButton,
  Chip,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Paper,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
  AutoAwesome as AutoAwesomeIcon,
  NavigateBefore,
  NavigateNext,
  ViewColumn,
  CompareArrows,
  BlurOn as BlurOnIcon,
  BlurOff as BlurOffIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/api';

// ─── Types ───────────────────────────────────────────────────────────────────

interface OutputJob {
  jobId: string;
  userId: string;
  template: { id: string; name: string; category: string } | null;
  beforeImageUrl: string | null;
  afterImageUrl: string | null;
  createdAt: string;
  metadata: { platform?: string; deviceId?: string; appVersion?: string; osVersion?: string; deviceModel?: string } | null;
  credits: number;
  processingTime: number | null;
}

interface FilterOption {
  id: string;
  name: string;
  category: string;
}

// ─── Before/After Slider ─────────────────────────────────────────────────────

const BeforeAfterSlider: React.FC<{ before: string; after: string }> = ({ before, after }) => {
  const [position, setPosition] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const pct = Math.max(1, Math.min(99, ((clientX - rect.left) / rect.width) * 100));
    setPosition(pct);
  }, []);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    containerRef.current?.setPointerCapture(e.pointerId);
    updatePosition(e.clientX);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.buttons === 0) return;
    updatePosition(e.clientX);
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        cursor: 'ew-resize',
        userSelect: 'none',
        touchAction: 'none',
        borderRadius: 1,
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
    >
      <Box
        component="img"
        src={after}
        alt="After"
        draggable={false}
        sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
      />
      <Box
        component="img"
        src={before}
        alt="Before"
        draggable={false}
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          display: 'block',
          clipPath: `inset(0 ${100 - position}% 0 0)`,
        }}
      />
      {/* Divider line */}
      <Box
        sx={{
          position: 'absolute',
          top: 0, bottom: 0,
          left: `${position}%`,
          transform: 'translateX(-50%)',
          width: 2,
          background: 'linear-gradient(to bottom, rgba(255,255,255,0.1) 0%, white 8%, white 92%, rgba(255,255,255,0.1) 100%)',
          boxShadow: '0 0 8px rgba(0,0,0,0.7)',
          pointerEvents: 'none',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 40, height: 40,
            borderRadius: '50%',
            bgcolor: 'white',
            boxShadow: '0 2px 12px rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px',
          }}
        >
          <Box sx={{ width: 0, height: 0, borderTop: '5px solid transparent', borderBottom: '5px solid transparent', borderRight: '6px solid #444' }} />
          <Box sx={{ width: 2, height: 14, bgcolor: '#ccc', borderRadius: 1 }} />
          <Box sx={{ width: 0, height: 0, borderTop: '5px solid transparent', borderBottom: '5px solid transparent', borderLeft: '6px solid #444' }} />
        </Box>
      </Box>
      <Box sx={{ position: 'absolute', top: 12, left: 12, pointerEvents: 'none' }}>
        <Chip label="Before" size="small" sx={{ bgcolor: 'rgba(0,0,0,0.65)', color: 'white', fontSize: 11, fontWeight: 600, backdropFilter: 'blur(4px)' }} />
      </Box>
      <Box sx={{ position: 'absolute', top: 12, right: 12, pointerEvents: 'none' }}>
        <Chip label="After" size="small" sx={{ bgcolor: 'rgba(0,0,0,0.65)', color: 'white', fontSize: 11, fontWeight: 600, backdropFilter: 'blur(4px)' }} />
      </Box>
    </Box>
  );
};

// ─── Side by Side View ────────────────────────────────────────────────────────

const SideBySideView: React.FC<{ before: string; after: string }> = ({ before, after }) => (
  <Box sx={{ display: 'flex', gap: 1, width: '100%', height: '100%' }}>
    <Box sx={{ flex: 1, position: 'relative' }}>
      <Box component="img" src={before} alt="Before" sx={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 1 }} />
      <Chip label="Before" size="small" sx={{ position: 'absolute', top: 8, left: 8, bgcolor: 'rgba(0,0,0,0.6)', color: 'white', fontSize: 11 }} />
    </Box>
    <Box sx={{ flex: 1, position: 'relative' }}>
      <Box component="img" src={after} alt="After" sx={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 1 }} />
      <Chip label="After" size="small" sx={{ position: 'absolute', top: 8, left: 8, bgcolor: 'rgba(0,0,0,0.6)', color: 'white', fontSize: 11 }} />
    </Box>
  </Box>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const CreativeLookOutputImages: React.FC = () => {
  const [jobs, setJobs] = useState<OutputJob[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(24);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [templateFilter, setTemplateFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [filterOptions, setFilterOptions] = useState<{ templates: FilterOption[]; userIds: string[] }>({ templates: [], userIds: [] });

  // Blur — default ON, persisted to localStorage
  const [blurEnabled, setBlurEnabled] = useState<boolean>(() => {
    const saved = localStorage.getItem('clOutputBlurEnabled');
    return saved !== null ? saved === 'true' : true;
  });
  const [previewBlurDisabled, setPreviewBlurDisabled] = useState(false);

  useEffect(() => {
    localStorage.setItem('clOutputBlurEnabled', blurEnabled.toString());
  }, [blurEnabled]);

  // Quick delete
  const [quickDeleteDialogOpen, setQuickDeleteDialogOpen] = useState(false);
  const [quickDeleteMinutes, setQuickDeleteMinutes] = useState<number | null>(null);
  const [quickDeleteLoading, setQuickDeleteLoading] = useState(false);
  const [quickDeleteResult, setQuickDeleteResult] = useState<number | null>(null);

  // Single delete
  const [deleteJobId, setDeleteJobId] = useState<string | null>(null);
  const [deleteSingleLoading, setDeleteSingleLoading] = useState(false);

  // Dialog / lightbox
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'slider' | 'sidebyside'>('slider');

  const selectedJob = selectedIndex !== null ? jobs[selectedIndex] : null;

  // Reset per-image blur override when switching images
  useEffect(() => {
    setPreviewBlurDisabled(false);
  }, [selectedIndex]);

  // Load filter options once
  useEffect(() => {
    apiService.getCreativeLookOutputFilters()
      .then(res => { if (res.success && res.data) setFilterOptions(res.data); })
      .catch(() => {});
  }, []);

  const loadJobs = useCallback(async (p = page) => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getCreativeLookOutput(
        p, limit,
        templateFilter || undefined,
        userFilter || undefined,
        startDate || undefined,
        endDate || undefined,
      );
      if (res.success && res.data) {
        setJobs(res.data.jobs || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
      } else {
        setError('Failed to load results');
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load results');
    } finally {
      setLoading(false);
    }
  }, [page, limit, templateFilter, userFilter, startDate, endDate]);

  useEffect(() => { loadJobs(); }, [page, limit, templateFilter, userFilter, startDate, endDate, loadJobs]);

  const handlePageChange = (_: any, value: number) => setPage(value);

  const openDialog = (index: number) => setSelectedIndex(index);
  const closeDialog = () => setSelectedIndex(null);

  const navigate = (dir: -1 | 1) => {
    if (selectedIndex === null) return;
    const next = selectedIndex + dir;
    if (next >= 0 && next < jobs.length) setSelectedIndex(next);
  };

  // Keyboard navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (selectedIndex === null) return;
      if (e.key === 'ArrowLeft')  navigate(-1);
      if (e.key === 'ArrowRight') navigate(1);
      if (e.key === 'Escape')     closeDialog();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedIndex]);

  const formatDate = (d: string) => new Date(d).toLocaleString();
  const formatTime = (ms: number | null) => ms != null ? `${(ms / 1000).toFixed(1)}s` : 'N/A';

  const handleQuickDelete = (minutes: number) => {
    setQuickDeleteMinutes(minutes);
    setQuickDeleteResult(null);
    setQuickDeleteDialogOpen(true);
  };

  const handleQuickDeleteConfirm = async () => {
    if (quickDeleteMinutes === null) return;
    setQuickDeleteLoading(true);
    try {
      const res = await apiService.deleteCreativeLookOutputsOlderThan(quickDeleteMinutes);
      if (res.success) {
        setQuickDeleteResult(res.data?.totalDeleted ?? 0);
        loadJobs(1);
      }
    } catch {}
    setQuickDeleteLoading(false);
  };

  const handleDeleteSingle = async () => {
    if (!deleteJobId) return;
    setDeleteSingleLoading(true);
    try {
      await apiService.deleteCreativeLookOutput(deleteJobId);
      setDeleteJobId(null);
      loadJobs();
    } catch {}
    setDeleteSingleLoading(false);
  };

  const formatQuickDeleteLabel = (minutes: number) => {
    if (minutes < 60) return `${minutes} min`;
    if (minutes < 1440) return `${minutes / 60} hour${minutes / 60 > 1 ? 's' : ''}`;
    return `${minutes / 1440} day${minutes / 1440 > 1 ? 's' : ''}`;
  };

  const isPreviewBlurred = blurEnabled && !previewBlurDisabled;

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center" gap={1}>
          <AutoAwesomeIcon color="primary" />
          <Typography variant="h4">Creative Look Results</Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={() => loadJobs()} variant="outlined" disabled={loading}>
          Refresh
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Template</InputLabel>
              <Select value={templateFilter} label="Template" onChange={(e) => { setTemplateFilter(e.target.value); setPage(1); }}>
                <MenuItem value="">All Templates</MenuItem>
                {filterOptions.templates.map(t => (
                  <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>User</InputLabel>
              <Select value={userFilter} label="User" onChange={(e) => { setUserFilter(e.target.value); setPage(1); }}>
                <MenuItem value="">All Users</MenuItem>
                {filterOptions.userIds.slice(0, 50).map(uid => (
                  <MenuItem key={uid} value={uid}>{uid}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Start Date" type="date" fullWidth size="small"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="End Date" type="date" fullWidth size="small"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          {/* Per page */}
          <Grid item xs={6} sm={4} md={1}>
            <FormControl fullWidth size="small">
              <InputLabel>Per Page</InputLabel>
              <Select
                value={limit}
                label="Per Page"
                onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
              >
                <MenuItem value={12}>12</MenuItem>
                <MenuItem value={24}>24</MenuItem>
                <MenuItem value={48}>48</MenuItem>
                <MenuItem value={96}>96</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={4} md={1}>
            <Button fullWidth variant="outlined" onClick={() => { setTemplateFilter(''); setUserFilter(''); setStartDate(''); setEndDate(''); setPage(1); }}>
              Clear
            </Button>
          </Grid>
          {/* Blur toggle */}
          <Grid item xs={6} sm={4} md={1}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={blurEnabled}
                  onChange={(e) => setBlurEnabled(e.target.checked)}
                  size="small"
                  icon={<BlurOffIcon fontSize="small" />}
                  checkedIcon={<BlurOnIcon fontSize="small" color="primary" />}
                />
              }
              label={<Typography variant="body2">Blur</Typography>}
              sx={{ m: 0 }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Quick Delete Section */}
      <Box display="flex" alignItems="center" gap={1} mb={2} flexWrap="wrap">
        <Typography variant="body2" fontWeight={600} color="text.secondary">
          Quick Delete:
        </Typography>
        {[
          { label: 'Delete > 10 min', minutes: 10 },
          { label: 'Delete > 1 hour', minutes: 60 },
          { label: 'Delete > 24 hours', minutes: 1440 },
          { label: 'Delete > 7 days', minutes: 10080 },
        ].map(({ label, minutes }) => (
          <Button
            key={minutes}
            variant="outlined"
            color="error"
            size="small"
            onClick={() => handleQuickDelete(minutes)}
            disabled={quickDeleteLoading}
          >
            {label}
          </Button>
        ))}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Grid */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : jobs.length === 0 ? (
        <Box display="flex" justifyContent="center" py={6}>
          <Typography color="text.secondary">No completed creative look results found</Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {jobs.map((job, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={job.jobId}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardActionArea onClick={() => openDialog(index)} sx={{ flex: 1 }}>
                  {/* Thumbnail: split before/after preview */}
                  <Box sx={{ position: 'relative', height: 200, bgcolor: 'grey.100', overflow: 'hidden' }}>
                    {job.beforeImageUrl && job.afterImageUrl ? (
                      <>
                        <Box
                          component="img"
                          src={job.beforeImageUrl}
                          alt="Before"
                          sx={{
                            position: 'absolute',
                            top: 0, left: 0,
                            width: '50%', height: '100%',
                            objectFit: 'cover',
                            filter: blurEnabled ? 'blur(8px)' : 'none',
                            transition: 'filter 0.3s ease',
                          }}
                        />
                        <Box
                          component="img"
                          src={job.afterImageUrl}
                          alt="After"
                          sx={{
                            position: 'absolute',
                            top: 0, right: 0,
                            width: '50%', height: '100%',
                            objectFit: 'cover',
                            filter: blurEnabled ? 'blur(8px)' : 'none',
                            transition: 'filter 0.3s ease',
                          }}
                        />
                        <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: '50%', width: 2, bgcolor: 'white', opacity: 0.8 }} />
                        <Chip label="B" size="small" sx={{ position: 'absolute', bottom: 6, left: 6, fontSize: 10, height: 18, bgcolor: 'rgba(0,0,0,0.5)', color: 'white' }} />
                        <Chip label="A" size="small" sx={{ position: 'absolute', bottom: 6, right: 6, fontSize: 10, height: 18, bgcolor: 'rgba(0,0,0,0.5)', color: 'white' }} />
                      </>
                    ) : (
                      <Box
                        component="img"
                        src={job.afterImageUrl || job.beforeImageUrl || ''}
                        alt="Result"
                        sx={{
                          width: '100%', height: '100%', objectFit: 'cover',
                          filter: blurEnabled ? 'blur(8px)' : 'none',
                          transition: 'filter 0.3s ease',
                        }}
                      />
                    )}
                  </Box>
                  {/* Card info */}
                  <Box sx={{ p: 1.5 }}>
                    {job.template && (
                      <Typography variant="body2" fontWeight="bold" noWrap>{job.template.name}</Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" display="block" noWrap>
                      {formatDate(job.createdAt)}
                    </Typography>
                    <Box display="flex" gap={0.5} mt={0.5} flexWrap="wrap" alignItems="center">
                      {job.template && <Chip label={job.template.category} size="small" variant="outlined" sx={{ fontSize: 10 }} />}
                      {job.metadata?.platform && <Chip label={job.metadata.platform} size="small" variant="outlined" sx={{ fontSize: 10 }} />}
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => { e.stopPropagation(); setDeleteJobId(job.jobId); }}
                        sx={{ ml: 'auto', p: 0.5 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" alignItems="center" mt={4} gap={2} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<NavigateBefore />}
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1 || loading}
          >
            Previous
          </Button>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            siblingCount={1}
          />
          <Button
            variant="outlined"
            endIcon={<NavigateNext />}
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages || loading}
          >
            Next
          </Button>
        </Box>
      )}

      {/* ─── Lightbox Dialog ─────────────────────────────────────────── */}
      <Dialog
        open={selectedIndex !== null}
        onClose={closeDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '90vh', display: 'flex', flexDirection: 'column', bgcolor: '#111' } }}
      >
        {selectedJob && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, px: 2, bgcolor: '#1a1a1a', color: 'white' }}>
              <Box display="flex" alignItems="center" gap={1}>
                <AutoAwesomeIcon sx={{ color: '#a78bfa', fontSize: 18 }} />
                <Typography variant="subtitle1" noWrap>
                  {selectedJob.template?.name || 'Creative Look'}
                </Typography>
                {selectedJob.template && (
                  <Chip label={selectedJob.template.category} size="small" sx={{ bgcolor: '#333', color: '#ccc', fontSize: 10 }} />
                )}
                <Typography variant="caption" sx={{ color: '#888', ml: 1 }}>
                  {selectedIndex !== null ? selectedIndex + 1 : 0} / {jobs.length}
                </Typography>
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                {/* View toggle */}
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(_, v) => v && setViewMode(v)}
                  size="small"
                  sx={{ bgcolor: '#333', borderRadius: 1 }}
                >
                  <Tooltip title="Slider comparison">
                    <ToggleButton value="slider" sx={{ color: viewMode === 'slider' ? '#a78bfa' : '#888', border: 'none', px: 1.5 }}>
                      <CompareArrows fontSize="small" />
                    </ToggleButton>
                  </Tooltip>
                  <Tooltip title="Side by side">
                    <ToggleButton value="sidebyside" sx={{ color: viewMode === 'sidebyside' ? '#a78bfa' : '#888', border: 'none', px: 1.5 }}>
                      <ViewColumn fontSize="small" />
                    </ToggleButton>
                  </Tooltip>
                </ToggleButtonGroup>

                {/* Prev / Next */}
                <IconButton onClick={() => navigate(-1)} disabled={selectedIndex === 0} sx={{ color: 'white' }} size="small">
                  <NavigateBefore />
                </IconButton>
                <IconButton onClick={() => navigate(1)} disabled={selectedIndex === jobs.length - 1} sx={{ color: 'white' }} size="small">
                  <NavigateNext />
                </IconButton>

                <IconButton onClick={closeDialog} sx={{ color: 'white' }} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>

            <DialogContent sx={{ flex: 1, p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {/* Image area — fills all available dialog height */}
              <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
                <Box
                  sx={{
                    position: 'absolute', inset: 0, p: 1.5,
                    filter: isPreviewBlurred ? 'blur(12px)' : 'none',
                    transition: 'filter 0.3s ease',
                  }}
                >
                  {selectedJob.beforeImageUrl && selectedJob.afterImageUrl ? (
                    viewMode === 'slider' ? (
                      <BeforeAfterSlider before={selectedJob.beforeImageUrl} after={selectedJob.afterImageUrl} />
                    ) : (
                      <SideBySideView before={selectedJob.beforeImageUrl} after={selectedJob.afterImageUrl} />
                    )
                  ) : (
                    <Box
                      component="img"
                      src={selectedJob.afterImageUrl || selectedJob.beforeImageUrl || ''}
                      alt="Result"
                      sx={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 1 }}
                    />
                  )}
                </Box>

                {/* Reveal overlay — shown only when blur is active in preview */}
                {isPreviewBlurred && (
                  <Box
                    onClick={() => setPreviewBlurDisabled(true)}
                    sx={{
                      position: 'absolute', inset: 0,
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer',
                      bgcolor: 'rgba(0,0,0,0.15)',
                    }}
                  >
                    <VisibilityIcon sx={{ color: 'white', fontSize: 48, opacity: 0.85, mb: 1 }} />
                    <Typography variant="body2" sx={{ color: 'white', opacity: 0.85, fontWeight: 600 }}>
                      Click to reveal
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Meta strip */}
              <Box sx={{ px: 2, py: 1, bgcolor: '#1a1a1a', display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography variant="caption" sx={{ color: '#aaa' }}>
                  {formatDate(selectedJob.createdAt)}
                </Typography>
                {selectedJob.metadata?.platform && (
                  <Chip label={selectedJob.metadata.platform} size="small" sx={{ bgcolor: '#333', color: '#ccc', fontSize: 10 }} />
                )}
                {/* {selectedJob.metadata?.appVersion && (
                  <Chip label={`v${selectedJob.metadata.appVersion}`} size="small" sx={{ bgcolor: '#333', color: '#ccc', fontSize: 10 }} />
                )} */}
                {/* {selectedJob.metadata?.deviceModel && (
                  <Chip label={selectedJob.metadata.deviceModel} size="small" sx={{ bgcolor: '#333', color: '#ccc', fontSize: 10 }} />
                )} */}
                <Typography variant="caption" sx={{ color: '#aaa' }}>
                  Credits: {selectedJob.credits}
                </Typography>
                {/* <Typography variant="caption" sx={{ color: '#aaa' }}>
                  Time: {formatTime(selectedJob.processingTime)}
                </Typography> */}
                {/* <Typography variant="caption" sx={{ color: '#888', ml: 'auto' }} noWrap>
                  User: {selectedJob.userId}
                </Typography> */}
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
      {/* Quick Delete Confirmation Dialog */}
      <Dialog open={quickDeleteDialogOpen} onClose={() => setQuickDeleteDialogOpen(false)}>
        <DialogTitle>Bulk Delete Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete all creative look results older than <strong>{quickDeleteMinutes !== null ? formatQuickDeleteLabel(quickDeleteMinutes) : ''}</strong>?
            <br /><br />
            This will permanently delete all matching input and output files from the server. This action cannot be undone.
          </DialogContentText>
          {quickDeleteResult !== null && (
            <Alert severity="success" sx={{ mt: 1 }}>
              {quickDeleteResult} result(s) deleted successfully.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuickDeleteDialogOpen(false)} disabled={quickDeleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleQuickDeleteConfirm}
            color="error"
            variant="contained"
            disabled={quickDeleteLoading}
          >
            {quickDeleteLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Single Delete Confirmation Dialog */}
      <Dialog open={!!deleteJobId} onClose={() => setDeleteJobId(null)}>
        <DialogTitle>Delete Creative Look Result</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this creative look result? This will remove both input and output files permanently.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteJobId(null)} disabled={deleteSingleLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteSingle}
            color="error"
            variant="contained"
            disabled={deleteSingleLoading}
          >
            {deleteSingleLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreativeLookOutputImages;
